import {Injectable} from '@angular/core';
import {PinService} from '@docma-mm-core/pin/pin-service';
import {Project} from '@docma-mm-core/project/project';
import {BpPin, BpPinLocationTargetEnum} from '../bp-common/models/bp-pin';
import {Coordinate} from '@docma-mm-core/plan/plan';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {Logger} from '@mydocma/common/src/lib/logger';
import {BehaviorSubject, Observable} from 'rxjs';

@UntilDestroy()
@Injectable()
export class MmPinsLoaderService{

  private pinsByUid = new Map<string, BpPin>();
  private project: Project;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadedProjectUidSubject = new BehaviorSubject<string | null>(null);

  public isLoading$: Observable<boolean> = this.loadingSubject.asObservable();
  public loadedProjectUid$: Observable<string | null> = this.loadedProjectUidSubject.asObservable();

  constructor(
    private readonly pinService: PinService,
    private readonly faultService: FaultService,
    private readonly logger: Logger
  ) {
    this.faultService.onFaultChanged
    .pipe(untilDestroyed(this))
    .subscribe((fault: ViewFault) => {
      if (fault.bpPin) {
        this.pinsByUid.set(fault.uid, fault.bpPin);
      }
    });

    this.faultService.onFaultDeleted
    .pipe(untilDestroyed(this))
    .subscribe(fault => {
      this.pinsByUid.delete(fault.uid);
    });
  }


  public addPin(p: BpPin ) {
    this.pinsByUid.set(p.ExternalId, p);
  }

  public reset() {
    this.pinsByUid.clear();
    this.loadedProjectUidSubject.next(null);
  }

  async loadPins(project: Project): Promise<BpPin[]> {
    this.reset();
    this.loadingSubject.next(true);

    try {
      // await new Promise(resolve => setTimeout(resolve, 25000));

      if (!project) {
        this.logger.warn('Cannot load pins: project is null or undefined', 'MmPinsLoaderService');
        return [];
      }

      if (!project.BPConnected) {
        this.logger.debug('Skipping pin loading: project is not BP-connected', 'MmPinsLoaderService');
        this.loadedProjectUidSubject.next(project.MyDocmaUid);
        return [];
      }

      this.logger.debug(`Loading pins for project: ${project.MyDocmaUid}`, 'MmPinsLoaderService');
      const pins = await this.pinService.getProjectPins(project);

      if (!pins || pins.length === 0) {
        this.logger.debug(`No pins found for project: ${project.MyDocmaUid}`, 'MmPinsLoaderService');
        this.loadedProjectUidSubject.next(project.MyDocmaUid);
        return [];
      }

      (pins || []).forEach(p => {
        this.pinsByUid.set(p.ExternalId, p);
      });

      this.logger.debug(`Successfully loaded ${pins.length} pins for project: ${project.MyDocmaUid}`, 'MmPinsLoaderService');
      this.loadedProjectUidSubject.next(project.MyDocmaUid);
      return pins;
    } catch (error) {
      this.logger.error(error, `Failed to load pins for project: ${project?.MyDocmaUid}`, 'MmPinsLoaderService');
      this.loadedProjectUidSubject.next(project?.MyDocmaUid || null);
      return [];
    } finally {
      this.loadingSubject.next(false);
    }
  }

  convertToCoordinate(pin: BpPin): Coordinate {
    const planLocation = (pin.Locations || []).find(l => l.Type === BpPinLocationTargetEnum.plan);
    if (planLocation) {
      return {
        PosX: planLocation.X * 100,
        PosY: planLocation.Y * 100,
        RelativeAnchorToParentPosX: planLocation.Marker?.Location?.X,
        RelativeAnchorToParentPosY: planLocation.Marker?.Location?.Y,
        FileId: planLocation.ModelId as any
      };
    }
    return null;
  }

  getPinByFaultUid(uid: string): BpPin {
    return this.pinsByUid.get(uid);
  }

  arePinsLoadedForProject(projectUid: string): boolean {
    return this.loadedProjectUidSubject.value === projectUid;
  }

  getPinsCount(): number {
    return this.pinsByUid.size;
  }

  isCurrentlyLoading(): boolean {
    return this.loadingSubject.value;
  }

}
